'use client'

import { ComboboxOption } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/20/solid'
import { cn } from '@mntn-dev/ui-utilities'
import type { ReactNode } from 'react'

import { themeTextColorMap } from '@mntn-dev/ui-theme'
import { useInputStyles } from '../input/use-input-styles.ts'
import { useSelectContext } from './use-select.ts'
import type { AnyValue } from '@mntn-dev/utility-types'

export type SelectOptionProps = Readonly<{
  value: string
  label: string
  icon?: ReactNode
  isLastItem?: boolean
  isPlaceholder?: boolean
  isHidden?: boolean
}>

export const SelectOption = ({
  value,
  label,
  icon,
  isPlaceholder,
  isHidden = false,
}: SelectOptionProps) => {
  const { getSelectProps } = useSelectContext()
  const { displayableOptions } = getSelectProps()
  const thisOption = displayableOptions?.find(
    // todo: fix this type
    (option: AnyValue) => option?.props?.value === value
  )
  const inputStyles = useInputStyles({ height: 'fit', minHeight: '12' })

  // If the option is not in the list of displayable options, don't display it
  if (!thisOption) {
    return
  }

  return (
    <ComboboxOption
      className={cn(isHidden ? 'hidden' : 'rounded-none last:rounded-b-md')}
      value={value}
    >
      {({ active, selected }) => {
        return (
          <div
            className={cn(
              inputStyles,
              'cursor-pointer gap-4 rounded-[inherit]',
              active && 'border-white/30'
            )}
          >
            {icon && (
              <span className="h-5 w-5">
                <span className="text-primary-blue shrink-0">{icon}</span>
              </span>
            )}

            <span
              className={cn(
                selected && !isPlaceholder ? 'font-bold' : 'font-normal',
                isPlaceholder && themeTextColorMap.disabled,
                'w-full'
              )}
            >
              {label}
            </span>
            {selected && !isPlaceholder ? (
              <span className="relative inset-y-0 left-3 flex items-center pr-4">
                <CheckIcon className="h-5 w-5" aria-hidden="true" />
              </span>
            ) : null}
          </div>
        )
      }}
    </ComboboxOption>
  )
}

SelectOption.displayName = 'SelectOption'
